import { DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  Input,
  LOCALE_ID,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { MatTableDataSource } from '@angular/material/table';
import {
  SummaryCardHorizontalItem,
  SummaryCardHorizontalComponent,
} from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  Calving,
  Cow,
  DayProduction,
  LactationProduction,
  Prediction,
} from '../../models/cow.model';
import * as CowActions from '../../store/actions/cow.actions';
import { getCow } from '../../store/selectors/cow.selectors';

@Component({
  selector: 'app-cow-details',
  templateUrl: './cow-details.component.html',
  styleUrls: ['./cow-details.component.scss'],
  imports: [
    SummaryCardHorizontalComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    TableComponent,
    TranslatePipe,
  ],
})
export class CowDetailsComponent implements OnInit, OnChanges, OnDestroy {
  private readonly locale = inject(LOCALE_ID);
  private readonly localeService = inject(LocaleService);
  private readonly store = inject<Store<SiteState>>(Store);

  @Input()
  public internationalCowId: string;
  public cow: Cow;
  public cowProperties: SummaryCardHorizontalItem[];
  public calvingDataSource = new MatTableDataSource<Calving>();
  public calvingTableConfig: TableConfig = {
    pagination: false,
    selectColumn: false,
    dataSource: this.calvingDataSource,
    columns: [
      {
        id: 'date',
        label: 'dairyresults.cow-details.calvings.date',
        type: TableColumnTypes.DATE,
      },
      {
        id: 'numberOfCalves',
        label: 'dairyresults.cow-details.calvings.numberOfCalves',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'parity',
        label: 'dairyresults.cow-details.calvings.parity',
        type: TableColumnTypes.NUMBER,
      },
    ],
  };
  public dayProductionDataSource = new MatTableDataSource<DayProduction>();
  public dayProductionTableConfig: TableConfig = {
    pagination: false,
    selectColumn: false,
    dataSource: this.dayProductionDataSource,
    columns: [
      {
        id: 'date',
        label: 'dairyresults.cow-details.day-production.date',
        type: TableColumnTypes.DATE,
      },
      {
        id: 'codeAantMelkingenDag',
        label: 'dairyresults.cow-details.day-production.codeAantMelkingenDag',
        type: TableColumnTypes.STRING,
      },
      {
        id: 'kgMilk',
        label: 'dairyresults.cow-details.day-production.kgMilk',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'proteinPercentage',
        label: 'dairyresults.cow-details.day-production.proteinPercentage',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'fatPercentage',
        label: 'dairyresults.cow-details.day-production.fatPercentage',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'lactosePercentage',
        label: 'dairyresults.cow-details.day-production.lactosePercentage',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'ureum',
        label: 'dairyresults.cow-details.day-production.ureum',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'somaticCellCount',
        label: 'dairyresults.cow-details.day-production.somaticCellCount',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'ketoseStatus',
        label: 'dairyresults.cow-details.day-production.ketose',
        type: TableColumnTypes.STRING,
      },
    ],
  };
  public lactationProductionDataSource = new MatTableDataSource<LactationProduction>();
  public lactationProductionTableConfig: TableConfig = {
    pagination: false,
    selectColumn: false,
    dataSource: this.lactationProductionDataSource,
    columns: [
      {
        id: 'dateCalved',
        label: 'dairyresults.cow-details.lactation-production.dateCalved',
        type: TableColumnTypes.DATE,
      },
      {
        id: 'kgProtein',
        label: 'dairyresults.cow-details.lactation-production.kgProtein',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'kgProtein305Days',
        label: 'dairyresults.cow-details.lactation-production.kgProtein305Days',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'proteinPercentage',
        label: 'dairyresults.cow-details.lactation-production.proteinPercentage',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'proteinPercentage305Days',
        label: 'dairyresults.cow-details.lactation-production.proteinPercentage305Days',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'kgMilk',
        label: 'dairyresults.cow-details.lactation-production.kgMilk',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'kgMilk305Days',
        label: 'dairyresults.cow-details.lactation-production.kgMilk305Days',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'kgFat',
        label: 'dairyresults.cow-details.lactation-production.kgFat',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'kgFat305Days',
        label: 'dairyresults.cow-details.lactation-production.kgFat305Days',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'fatPercentage',
        label: 'dairyresults.cow-details.lactation-production.fatPercentage',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'fatPercentage305Days',
        label: 'dairyresults.cow-details.lactation-production.fatPercentage305Days',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'netProduction',
        label: 'dairyresults.cow-details.lactation-production.netProduction',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'isk',
        label: 'dairyresults.cow-details.lactation-production.isk',
        type: TableColumnTypes.NUMBER,
      },
    ],
  };
  public predictionProductionDataSource = new MatTableDataSource<Prediction>();
  public predictionTableConfig: TableConfig = {
    pagination: false,
    selectColumn: false,
    dataSource: this.predictionProductionDataSource,
    columns: [
      {
        id: 'mutation',
        label: 'dairyresults.cow-details.prediction.mutation',
        type: TableColumnTypes.DATE,
      },
      {
        id: 'daysPregnant',
        label: 'dairyresults.cow-details.prediction.daysPregnant',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'calvingDateExpected',
        label: 'dairyresults.cow-details.prediction.calvingDateExpected',
        type: TableColumnTypes.DATE,
      },
      {
        id: 'lactation',
        label: 'dairyresults.cow-details.prediction.lactation',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'parity',
        label: 'dairyresults.cow-details.prediction.parity',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'pregnantStatus',
        label: 'dairyresults.cow-details.prediction.pregnantStatus',
        type: TableColumnTypes.STRING,
      },
      {
        id: 'kgMilk',
        label: 'dairyresults.cow-details.prediction.kgMilk',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'fatPercentage',
        label: 'dairyresults.cow-details.prediction.fatPercentage',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'proteinPercentage',
        label: 'dairyresults.cow-details.prediction.proteinPercentage',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'kgMilk3Weeks',
        label: 'dairyresults.cow-details.prediction.kgMilk3Weeks',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'fatPercentage3Weeks',
        label: 'dairyresults.cow-details.prediction.fatPercentage3Weeks',
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'proteinPercentage3Weeks',
        label: 'dairyresults.cow-details.prediction.proteinPercentage3Weeks',
        type: TableColumnTypes.NUMBER,
      },
    ],
  };
  public addRemoveDataSource = new MatTableDataSource();
  public addRemoveTableConfig: TableConfig = {
    pagination: false,
    selectColumn: false,
    dataSource: this.addRemoveDataSource,
    columns: [
      {
        id: 'mutation',
        label: 'dairyresults.cow-details.add-remove.mutation',
        type: TableColumnTypes.DATE,
      },
      {
        id: 'type',
        label: 'dairyresults.cow-details.add-remove.type',
        type: TableColumnTypes.STRING,
      },
      {
        id: 'code',
        label: 'dairyresults.cow-details.add-remove.code',
        type: TableColumnTypes.STRING,
      },
    ],
  };
  public productionStatusDataSource = new MatTableDataSource();
  public productionStatusTableConfig: TableConfig = {
    pagination: false,
    selectColumn: false,
    dataSource: this.productionStatusDataSource,
    columns: [
      {
        id: 'mutation',
        label: 'dairyresults.cow-details.production-status.mutation',
        type: TableColumnTypes.DATE,
      },
      {
        id: 'statusMilkProduction',
        label: 'dairyresults.cow-details.production-status.statusMilkProduction',
        type: TableColumnTypes.STRING,
      },
    ],
  };
  public inseminationDataSource = new MatTableDataSource();
  public inseminationTableConfig: TableConfig = {
    pagination: false,
    selectColumn: false,
    dataSource: this.inseminationDataSource,
    columns: [
      {
        id: 'mutation',
        label: 'dairyresults.cow-details.inseminations.mutation',
        type: TableColumnTypes.DATE,
      },
      {
        id: 'inseminationDate',
        label: 'dairyresults.cow-details.inseminations.inseminationDate',
        type: TableColumnTypes.DATE,
      },
      {
        id: 'bullCowId',
        label: 'dairyresults.cow-details.inseminations.bullCowId',
        type: TableColumnTypes.STRING,
      },
      {
        id: 'type',
        label: 'dairyresults.cow-details.inseminations.type',
        type: TableColumnTypes.STRING,
      },
      {
        id: 'status',
        label: 'dairyresults.cow-details.inseminations.status',
        type: TableColumnTypes.STRING,
      },
    ],
  };

  private getCow$ = this.store.select(getCow);
  private readonly destroy$ = new Subject<void>();
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['internationalCowId'].currentValue !== changes['internationalCowId'].previousValue
    ) {
      this.store.dispatch(
        CowActions.loadCowRequest({ internationalCowId: this.internationalCowId }),
      );
    }
  }

  ngOnInit(): void {
    this.getCow$
      .pipe(
        filter((cow) => !!cow && cow.internationalCowId === this.internationalCowId),
        takeUntil(this.destroy$),
      )
      .subscribe((cow) => {
        this.cow = cow;
        this.calvingDataSource.data = [...cow.mutations.calvings].sort(this.dateSort);
        this.dayProductionDataSource.data = [...cow.mutations.dayProductions]
          .map((a) => ({
            ...a,
            ketoseStatus: this.localeService.translate(
              `dairyresults.cow-details.day-production.ketose-status-${a.ketose}`,
            ),
          }))
          .sort(this.dateSort);
        this.lactationProductionDataSource.data = [...cow.mutations.lactationProductions].sort(
          this.dateSort,
        );
        this.predictionProductionDataSource.data = [...cow.mutations.predictions]
          .map((a) => ({
            ...a,
            pregnantStatus: this.localeService.translate(
              `dairyresults.cow-details.prediction.pregnant-status-${a.pregnantStatus}`,
            ),
          }))
          .sort(this.dateSort);
        const additions = [...cow.mutations.additions].map((a) => ({
          type: this.localeService.translate('dairyresults.cow-details.add-remove.add'),
          mutation: a.date,
          code: this.localeService.translate(
            `dairyresults.cow-details.add-remove.add-code-${a.code}`,
          ),
        }));
        const removals = [...cow.mutations.removals].map((a) => ({
          type: this.localeService.translate('dairyresults.cow-details.add-remove.remove'),
          mutation: a.date,
          code: this.localeService.translate(
            `dairyresults.cow-details.add-remove.remove-code-${a.code}`,
          ),
        }));
        this.productionStatusDataSource.data = [
          ...cow.mutations.productionStatusMutations.map((a) => ({
            mutation: a.mutation,
            statusMilkProduction: this.localeService.translate(
              `dairyresults.cow-details.production-status.status-${a.statusMilkProduction}`,
            ),
          })),
        ].sort(this.dateSort);
        this.inseminationDataSource.data = [
          ...cow.mutations.inseminations.map((a) => ({
            ...a,
            status: this.localeService.translate(
              `dairyresults.cow-details.inseminations.status-${a.status}`,
            ),
            type: this.localeService.translate(
              `dairyresults.cow-details.inseminations.type-${a.type}`,
            ),
          })),
        ].sort(this.dateSort);
        this.addRemoveDataSource.data = [...additions, ...removals].sort(this.dateSort);
        this.cowProperties = [
          {
            label: this.localeService.translate('dairyresults.cow-details.properties.number'),
            value: cow.number ?? '-',
          },
          {
            label: this.localeService.translate(
              'dairyresults.cow-details.properties.internationalCowId',
            ),
            value: cow.internationalCowId ?? '-',
          },
          {
            label: this.localeService.translate('dairyresults.cow-details.properties.name'),
            value: cow.name,
          },
          {
            label: this.localeService.translate('dairyresults.cow-details.properties.birthDate'),
            value: new DatePipe(this.locale).transform(
              new Date(cow.birthDate),
              this.localeService.translate('dairyresults.cow-details.properties.birthDateFormat'),
            ),
          },
          {
            label: this.localeService.translate('dairyresults.cow-details.properties.ageInYears'),
            value: new DecimalPipe(this.locale).transform(
              this.convertToAgeWithMonthDecimals(cow.ageInYears),
              '1.2-2',
            ),
          },
          {
            label: this.localeService.translate('dairyresults.cow-details.properties.gender'),
            value: this.translateReplace(
              cow.gender,
              'dairyresults.cow-details.properties.gender-',
              '-',
            ),
          },
          {
            label: this.localeService.translate(
              'dairyresults.cow-details.properties.productionPurpose',
            ),
            value: this.translateReplace(
              cow.productionPurpose,
              'dairyresults.cow-details.properties.productionPurpose-',
              '-',
            ),
          },
          {
            label: this.localeService.translate(
              'dairyresults.cow-details.properties.statusMilkProduction',
            ),
            value: this.translateReplace(
              cow.mutations.lastProductionStatus?.statusMilkProduction,
              'dairyresults.cow-details.properties.statusMilkProduction-',
              '-',
            ),
          },
        ];
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private convertToAgeWithMonthDecimals(age: number): number {
    const fractionPart = age - Math.trunc(Math.round(age * 100) / 100);
    const years = age - fractionPart;
    const months = Math.round(fractionPart * 12) / 100;
    return months === 0.12 ? years + 1 : years + months;
  }

  private translateReplace = (value: string, key: string, replacement: string): string =>
    value !== null ? this.localeService.translate(`${key}${value}`) : replacement;

  private dateSort(a: SortMutationRecord, b: SortMutationRecord): number {
    const aDate = new Date(a.mutation);
    const bDate = new Date(b.mutation);
    if (aDate === bDate) {
      return 0;
    }
    return aDate < bDate ? 1 : -1;
  }
}

interface SortMutationRecord {
  mutation: string;
}
